import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";

import { CHECKOUT_FIELDS } from "@/shared/gift-cards.constants";

import EVENTS from "@/analytics/events";
import { trackPageLoad } from "@/analytics/tracking";

import { useCurrentLocale } from "@/context/LanguageContext";

import { KINDS, SIZES } from "@/components/buttons/buttons.constants";
import LinkButton from "@/components/buttons/link-button";

import { useIsArcade } from "@/hooks/useIsArcade";

import CheckCircleIconArcade from "../../assets/icons/check-circle-arcade.svg";
import CheckCircleIcon from "../../assets/icons/check-circle.svg";
import { GiftCardCheckoutNotification } from "./gift-card-checkout-notification";
import styles from "./payment-confirmation-layout.module.scss";

const SHOW_CLOSE_BUTTON = "showCloseButton";

type Props = {
    paymentId: string;
    orderNumber: string;
    email: string;
    sku: string;
    variant?: "default" | "short";
};

const PaymentConfirmationLayout: React.FC<Props> = ({
    paymentId,
    orderNumber,
    email,
    sku,
    variant = "default",
}) => {
    const { t } = useTranslation();
    const locale = useCurrentLocale();
    const router = useRouter();
    const isArcade = useIsArcade();

    useEffect(() => {
        trackPageLoad(router.asPath, locale, EVENTS.GIFT_CARD_CHECKOUT_COMPLETED, {
            sku,
            success: true,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /*
     * When using Safari (desktop or iOS) the Afterpay SDK redirects the user to portal.afterpay.com instead of
     * opening a popup and on afterpay.com.
     * Once portal.afterpay.com redirects the user back to the gift card page it will add status and orderToken
     * query parameters the the URL.
     * The state and context are reset, but the status and orderToken query parameters let us resume the checkout.
     * Once the payment has been confirmed delete the checkout data from session storage.
     * */
    sessionStorage.removeItem(CHECKOUT_FIELDS);

    if (variant === "short") {
        window["AfterpayReactNativeWebView"]?.postMessage(
            JSON.stringify({ type: SHOW_CLOSE_BUTTON, payload: {} })
        );

        return (
            <GiftCardCheckoutNotification
                icon={isArcade ? <CheckCircleIconArcade /> : <CheckCircleIcon />}
                headerText={t("giftCards.paymentConfirmation.headerV2")}
                messageText={t("giftCards.paymentConfirmation.message")}
                email={email}
            />
        );
    }

    return (
        <div className={styles.parentContainer}>
            <>
                <div>
                    <CheckCircleIcon />
                </div>
                <h2 className={styles.header}>{t("giftCards.paymentConfirmation.header")}</h2>
                <div className={styles.informationContainer}>
                    <h4>{t("giftCards.paymentConfirmation.paymentId")}</h4>
                    <span>{paymentId}</span>
                </div>
                {orderNumber && (
                    <div className={styles.informationContainer}>
                        <h4>{t("giftCards.paymentConfirmation.orderNumber")}</h4>
                        <span>{orderNumber}</span>
                    </div>
                )}
                <div className={styles.informationContainer}>
                    <h4>{t("giftCards.paymentConfirmation.recipientEmail")}</h4>
                    <span>{email}</span>
                </div>
                <div className={styles.shopGiftCardsButtonContainer}>
                    <LinkButton
                        fluid={true}
                        kind={KINDS.PRIMARY}
                        label={t("giftCards.buttons.giftCards")}
                        size={SIZES.LARGE}
                        url="/gift-cards"
                    />
                </div>
                <div className={styles.homeButtonContainer}>
                    <LinkButton
                        fluid={true}
                        kind={KINDS.BLACK}
                        label={t("giftCards.buttons.home")}
                        size={SIZES.LARGE}
                        url="/"
                    />
                </div>
            </>
        </div>
    );
};

export default PaymentConfirmationLayout;
